import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import { Tabs, Tab } from 'components/common/tabs/tabs';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import _ from 'lodash';

const styles = {
  numberInput: {
    display: 'inline',
    width: '70px',
    textAlign: 'center'
  }
};

export default function ReportPeriod(props) {
  const {
    types, selectedReportTypeId,
    relativePeriodValue, onChangeRelativePeriod,
    relativeOffsetStartValue, setRelativeOffsetStartValue,
    relativeOffsetEndValue, setRelativeOffsetEndValue,
    absolutePeriodStartValue, onChangeAbsolutePeriodStartValue,
    absolutePeriodEndValue, onChangeAbsolutePeriodEndValue,
    isPeriodRelative, setIsPeriodRelative
  } = props;

  const [selectedReport, setSelectedReport] = useState({});
  const [relativeDateOptions, setRelativeDateOptions] = useState(null);
  const [absoluteDateOptions, setAbsoluteDateOptions] = useState(null);

  useEffect(() => {
    const reportType = _.find(types, { id: selectedReportTypeId });
    setSelectedReport(reportType);
    setRelativeDateOptions(BackofficeReportsHelper.getRelativeDateOptions(reportType));
    setAbsoluteDateOptions(BackofficeReportsHelper.getAbsoluteDateOptions(reportType));
  }, [types, selectedReportTypeId]);

  const renderRelativePeriodValues = () => {
    if (relativeDateOptions == null) {
      return <div>No relative date options exist for this report.</div>;
    }
    else {
      const typesGroups = _.groupBy(relativeDateOptions, 'group');
      const groups = [];
      for (const groupName in typesGroups) {
        groups.push({ groupName, items: typesGroups[groupName] });
      }

      const errorText = BackofficeReportsHelper.getRelativeDateError(selectedReport, relativePeriodValue, relativeOffsetStartValue, relativeOffsetEndValue);
      const hasError = errorText && errorText.length > 0;
      return (
        <>
          <Form.Group>
            <Form.Label required>Relative Value</Form.Label>
            <Inputs.Select
              required
              placeholder='Please select relative date'
              description='Relative Value'
              value={relativePeriodValue}
              onValueChange={onChangeRelativePeriod}
              disabled={!selectedReportTypeId}
            >
              {groups.map((group, index) => (
                <Inputs.OptionGroup label={group.groupName} key={`group${index}`}>
                  {group.items.map((item, itemIndex) => (
                    <Inputs.Option value={item.key} label={item.name} key={`group${index}_item${itemIndex}`} />
                  ))}
                </Inputs.OptionGroup>
              ))}
            </Inputs.Select>
          </Form.Group>
          {relativePeriodValue !== 'daysoff' ? null : (
            <div className='mt-2 ml-4'>
              <Inputs.Integer min='0' max='999' value={relativeOffsetStartValue} onValueChange={setRelativeOffsetStartValue} isInvalid={hasError} style={styles.numberInput} />
              <span className='ml-2'>days ago</span>
            </div>
          )}
          {relativePeriodValue !== 'daysrangeoff' ? null : (
            <div className='mt-2 ml-4'>
              <Inputs.Integer min='0' max='999' value={relativeOffsetStartValue} onValueChange={setRelativeOffsetStartValue} isInvalid={hasError} style={styles.numberInput} />
              <span className='mx-2'>to</span>
              <Inputs.Integer min='0' max='999' value={relativeOffsetEndValue} onValueChange={setRelativeOffsetEndValue} isInvalid={hasError} style={styles.numberInput} />
              <span className='ml-2'>days ago</span>
            </div>
          )}
          {relativePeriodValue !== 'weeksoff' ? null : (
            <div className='mt-2 ml-4'>
              <Inputs.Integer min='0' max='99' value={relativeOffsetStartValue} onValueChange={setRelativeOffsetStartValue} isInvalid={hasError} style={styles.numberInput} />
              <span className='ml-2'>weeks ago</span>
            </div>
          )}
          {!hasError ? null :
            <div className='invalid-feedback d-block ml-4'>{errorText}</div>
          }
        </>
      );
    }
  };

  const renderAbsolutePeriodValues = () => {
    if (absoluteDateOptions == null) {
      return <div>No absolute date options exist for this report.</div>;
    }
    else if (absoluteDateOptions.length === 2) {
      const errorText = BackofficeReportsHelper.getAbsoluteDateError(selectedReport, absolutePeriodStartValue, absolutePeriodEndValue);
      const hasError = errorText && errorText.length > 0;
      return (
        <>
          <Form.Group>
            <Form.Label required>Report Start Date</Form.Label>
            <Inputs.Date
              required
              placeholder='Please select report start date'
              description='Report Start Date'
              value={absolutePeriodStartValue}
              onChange={onChangeAbsolutePeriodStartValue}
              isInvalid={hasError}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label required>Report End Date</Form.Label>
            <Inputs.Date
              required
              placeholder='Please select report start date'
              description='Report End Date'
              value={absolutePeriodEndValue}
              altDate={absolutePeriodStartValue}
              onChange={onChangeAbsolutePeriodEndValue}
              isInvalid={hasError}
              feedback={errorText}
            />
          </Form.Group>
        </>
      );
    }
    else {
      return (
        <Form.Group>
          <Form.Label required>Report Date</Form.Label>
          <Inputs.Date
            required
            placeholder='Please select report date'
            description='Report Date'
            value={absolutePeriodStartValue}
            onChange={onChangeAbsolutePeriodStartValue}
          />
        </Form.Group>
      );
    }
  };

  if (relativeDateOptions == null || relativeDateOptions.length === 0) {
    if (absoluteDateOptions == null || absoluteDateOptions.length === 0) {
      return (
        <Form.Group>
          <Form.Label>Report Period</Form.Label>
          <Inputs.Plaintext>No date options exist for selected report</Inputs.Plaintext>
        </Form.Group>
      );
    }
  }

  return (
    <Form.Group>
      <Form.Label>Report Period</Form.Label>
      <Tabs defaultSelection={isPeriodRelative ? 'relative' : 'absolute'} onSelect={(key) => setIsPeriodRelative(key === 'relative')}>
        <Tab value='relative' title='Relative'>
          {renderRelativePeriodValues()}
        </Tab>
        <Tab value='absolute' title='Absolute'>
          {renderAbsolutePeriodValues()}
        </Tab>
      </Tabs>
    </Form.Group>
  );
}